import React, { useEffect } from "react";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "../Navbar";
import { CTASection, Footer } from "../CTAFooter";
import { successStories, SITE } from "../../data/mock";
import { Reveal } from "../ui/Reveal";

const SuccessStories = () => {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = `Real Campaigns, Real Numbers | ${SITE.name}`;
    return () => {
      document.title = prevTitle;
    };
  }, []);

  if (!successStories || successStories.length === 0) {
    return (
      <div className="min-h-screen bg-white font-sans text-neutral-900">
        <Navbar />
        <div className="max-w-2xl mx-auto px-6 py-32 text-center">
          <h1 className="text-[28px] font-semibold text-neutral-950">Success stories coming soon</h1>
          <p className="mt-3 text-neutral-600">Case studies are being written up — check back shortly.</p>
        </div>
        <Footer />
      </div>
    );
  }

  const previewImage = (story) => story.thumbnailImage || story.bannerImage;

  return (
    <div className="min-h-screen bg-white font-sans text-neutral-900">
      <Navbar />

      {/* ---- Hero — white background, dot-badge matching the site's hero convention ---- */}
      <section className="pt-16 pb-14 text-center">
        <div className="max-w-[1250px] mx-auto px-6">
          <Reveal>
            <span className="inline-flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase text-neutral-500 border border-black/10 rounded-full px-3 py-1.5 bg-white/60">
              <span className="w-1.5 h-1.5 rounded-full bg-black"></span> Success stories
            </span>
            <h1 className="mt-6 text-[32px] sm:text-[48px] leading-[1.1] tracking-[-0.02em] font-semibold text-neutral-950 sm:whitespace-nowrap">
              Results that speak louder than promises.
            </h1>
            <p className="mt-5 text-[17px] text-neutral-600 max-w-xl mx-auto">
              Real outcomes from real campaigns, built on predictable revenue systems, not vanity metrics.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ---- Card grid — its own distinct gray section ---- */}
      <section className="py-16 bg-neutral-50">
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {successStories.map((story, i) => (
              <Reveal key={story.slug} delay={(i % 4) * 0.08}>
                {/* Whole card is a single Link — every part of it is clickable,
                    not just the "Read the full story" text at the bottom. */}
                <Link
                  to={`/success-stories/${story.slug}`}
                  className="group block bg-white rounded-3xl border border-black/5 overflow-hidden hover:shadow-lg transition-all duration-300 h-full flex flex-col"
                >
                  <div className="h-[213px] bg-neutral-100 flex-shrink-0">
                    {previewImage(story) ? (
                      <img
                        src={previewImage(story)}
                        alt={story.clientName}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-neutral-400 text-sm">
                        Image coming soon
                      </div>
                    )}
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <span className="text-[11px] font-mono uppercase tracking-wide text-neutral-500">
                      {story.tag}
                    </span>
                    <h3 className="mt-3 text-lg font-semibold text-neutral-950 leading-snug">
                      {story.listingTitle}
                    </h3>
                    <p className="mt-2 text-sm text-neutral-600 leading-relaxed flex-1">
                      {story.listingSummary}
                    </p>
                    <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-neutral-900 group-hover:gap-2.5 transition-all">
                      Read the full story <ArrowUpRight className="w-4 h-4" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
      <Footer />
    </div>
  );
};

export default SuccessStories;
